import { useEffect, useMemo, useState } from "react";
import { formatTimeAgo, parseInstant } from "../lib/dateTime";
import { supabase } from "../supabaseClient";

const READ_STATE_KEY = "echo_jham_admin_notif_read_state_v1";

const getReadState = () => {
  try {
    const raw = localStorage.getItem(READ_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const setReadState = (next) => {
  try {
    localStorage.setItem(READ_STATE_KEY, JSON.stringify(next || {}));
  } catch {
    // Ignore storage write failures.
  }
};

const inferRouteFromContent = ({ title = "", message = "", meta = "" }) => {
  const haystack = `${title} ${message} ${meta}`.toLowerCase();
  const bookingMatch = haystack.match(/booking[^\d]*(\d+)/i) || haystack.match(/#(\d+)/);

  if (haystack.includes("booking")) {
    if (bookingMatch?.[1]) {
      return `/admin/bookings/${bookingMatch[1]}`;
    }
    return "/admin/bookings";
  }
  if (haystack.includes("order") || haystack.includes("ticket")) return "/admin/orders";
  if (haystack.includes("event") || haystack.includes("box office")) return "/admin/liveevents";
  if (haystack.includes("customer")) return "/admin/customers";
  if (haystack.includes("client")) return "/admin/clients";

  return "/admin/dashboard";
};

const asEpoch = (value, fallback = 0) => {
  const parsed = parseInstant(value);
  return parsed ? parsed.getTime() : fallback;
};

const buildNotifications = ({ eventsData = [], bookingsData = [] }, readMap) => {
  const eventNotif = {
    id: "notif-events-live",
    title: `${eventsData.length} published event${eventsData.length === 1 ? "" : "s"} live`,
    meta: "Box Office",
    route: "/admin/liveevents",
    read: Boolean(readMap["notif-events-live"]),
  };

  const bookingNotif = {
    id: "notif-bookings-pending",
    title: `${bookingsData.length} booking${bookingsData.length === 1 ? "" : "s"} awaiting action`,
    meta: "Client Services",
    route: "/admin/bookings",
    read: Boolean(readMap["notif-bookings-pending"]),
  };

  return [eventNotif, bookingNotif];
};

const buildLegacyNotifications = ({ eventsData = [], bookingsData = [], ticketsData = [] }, readMap) => {
  const eventItems = [];

  (eventsData || []).forEach((eventRow) => {
    const title = eventRow.title || "Untitled event";
    const createdAt = eventRow.created_at || eventRow.updated_at;
    const updatedAt = eventRow.updated_at || eventRow.created_at;
    const status = String(eventRow.status || "").toLowerCase();

    if (createdAt) {
      const id = `legacy-event-created-${eventRow.id}`;
      eventItems.push({
        id,
        title: `Event created: ${title}`,
        meta: `Events • ${formatTimeAgo(createdAt)}`,
        route: "/admin/liveevents",
        createdAt,
        sortTime: asEpoch(createdAt),
        read: Boolean(readMap[id]),
      });
    }

    if (status === "archived") {
      const id = `legacy-event-archived-${eventRow.id}`;
      eventItems.push({
        id,
        title: `Event archived: ${title}`,
        meta: `Events • ${formatTimeAgo(updatedAt)}`,
        route: "/admin/liveevents",
        createdAt: updatedAt,
        sortTime: asEpoch(updatedAt),
        read: Boolean(readMap[id]),
      });
    }
  });

  const bookingItems = (bookingsData || []).map((bookingRow) => {
    const status = String(bookingRow.status || "new")
      .toLowerCase()
      .replace(/\s+/g, "_");
    const label = status === "in_review" ? "In Review" : status.charAt(0).toUpperCase() + status.slice(1);
    const id = `legacy-booking-${bookingRow.id}-${status}`;
    const sortSource = bookingRow.updated_at || bookingRow.created_at;

    const requesterName = String(bookingRow.requester_name || "Client").trim() || "Client";
    const eventName = String(bookingRow.event_name || bookingRow.request_code || "Booking request").trim();
    const isNewBooking = ["new", "pending_review", "in_review"].includes(status);

    return {
      id,
      title: isNewBooking
        ? `New booking request: ${eventName}`
        : `Booking status: ${label}`,
      meta: isNewBooking
        ? `${requesterName} • Bookings • ${formatTimeAgo(sortSource)}`
        : `Bookings • ${formatTimeAgo(sortSource)}`,
      route: "/admin/bookings",
      createdAt: sortSource || null,
      sortTime: asEpoch(sortSource),
      read: Boolean(readMap[id]),
    };
  });

  const orderBuckets = new Map();
  (ticketsData || []).forEach((ticket) => {
    const parsed = parseInstant(ticket.created_at);
    if (!parsed) return;

    parsed.setMilliseconds(0);
    const secondKey = parsed.toISOString();
    const eventTitle = ticket.ticket_tiers?.events?.title || "Event";
    const key = `${eventTitle}|${secondKey}`;

    if (!orderBuckets.has(key)) {
      orderBuckets.set(key, {
        id: `legacy-order-${key}`,
        eventTitle,
        count: 0,
        createdAt: parsed.toISOString(),
      });
    }

    orderBuckets.get(key).count += 1;
  });

  const orderItems = Array.from(orderBuckets.values()).map((bucket) => ({
    id: bucket.id,
    title: `Order placed: ${bucket.eventTitle}`,
    meta: `Orders • ${bucket.count} ticket${bucket.count === 1 ? "" : "s"} • ${formatTimeAgo(bucket.createdAt)}`,
    route: "/admin/orders",
    createdAt: bucket.createdAt,
    sortTime: asEpoch(bucket.createdAt),
    read: Boolean(readMap[bucket.id]),
  }));

  return [...eventItems, ...bookingItems, ...orderItems]
    .sort((a, b) => b.sortTime - a.sortTime)
    .slice(0, 40)
    .map(({ sortTime, ...rest }) => rest);
};

const mapTableNotifications = (rows) => {
  return (rows || []).map((row) => ({
    id: String(row.id),
    title: row.title || row.message || "Notification",
    meta: row.meta || "System",
    route: inferRouteFromContent({
      title: row.title || row.message || "",
      message: row.message || "",
      meta: row.meta || "",
    }),
    read: Boolean(row.is_read),
    createdAt: row.created_at || null,
  }));
};

export const useAdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [usingTableSource, setUsingTableSource] = useState(false);

  useEffect(() => {
    let disposed = false;

    const refresh = async () => {
      const notifQuery = await supabase
        .from("notifications")
        .select("id, title, message, meta, is_read, created_at")
        .order("created_at", { ascending: false })
        .limit(20);

      const [{ data: eventsData, error: eventsError }, { data: bookingsData, error: bookingsError }, { data: ticketsData, error: ticketsError }] = await Promise.all([
        supabase.from("events").select("id, title, status, created_at, updated_at").order("updated_at", { ascending: false }).limit(60),
        supabase.from("bookings").select("id, request_code, requester_name, event_name, status, created_at, updated_at").order("updated_at", { ascending: false }).limit(60),
        supabase
          .from("tickets")
          .select("id, created_at, ticket_tiers(name, events(title))")
          .order("created_at", { ascending: false })
          .limit(80),
      ]);

      if (disposed) return;

      if (eventsError || bookingsError || ticketsError) {
        console.error("Failed to refresh admin notifications:", eventsError || bookingsError || ticketsError);
      }

      const readMap = getReadState();
      const legacyItems = buildLegacyNotifications(
        {
          eventsData: eventsData || [],
          bookingsData: bookingsData || [],
          ticketsData: ticketsData || [],
        },
        readMap
      );

      if (!notifQuery.error) {
        setUsingTableSource(true);

        const tableItems = mapTableNotifications(notifQuery.data || []).map((item) => ({
          ...item,
          sortTime: asEpoch(item.createdAt),
        }));

        const legacyWithSort = legacyItems.map((item, index) => ({
          ...item,
          sortTime: Date.now() - 1000 - index,
        }));

        const merged = [...tableItems, ...legacyWithSort]
          .sort((a, b) => b.sortTime - a.sortTime)
          .slice(0, 40)
          .map(({ sortTime, ...rest }) => rest);

        setNotifications(merged);
        return;
      }

      setUsingTableSource(false);
      const next = buildNotifications(
        {
          eventsData: (eventsData || []).filter((item) => String(item.status || "").toLowerCase() === "published"),
          bookingsData: (bookingsData || []).filter((item) => {
            const status = String(item.status || item.booking_status || "").toLowerCase();
            return ["new", "in_review"].includes(status);
          }),
        },
        readMap
      );

      const fallbackWithSort = [...legacyItems, ...next].map((item, index) => ({
        ...item,
        sortTime: item.sortTime || Date.now() - index,
      }));

      const fallbackSorted = fallbackWithSort
        .sort((a, b) => b.sortTime - a.sortTime)
        .slice(0, 40)
        .map(({ sortTime, ...rest }) => rest);

      setNotifications(fallbackSorted);
    };

    refresh();

    const channel = supabase
      .channel("admin-notifications-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, refresh)
      .subscribe();

    return () => {
      disposed = true;
      channel.unsubscribe();
    };
  }, []);

  const unreadCount = useMemo(() => notifications.filter((item) => !item.read).length, [notifications]);

  const markAllAsRead = async () => {
    if (usingTableSource) {
      const unreadIds = notifications
        .filter((item) => !item.read && !String(item.id).startsWith("legacy-"))
        .map((item) => item.id);

      if (unreadIds.length) {
        const { error } = await supabase
          .from("notifications")
          .update({ is_read: true })
          .in("id", unreadIds);

        if (error) {
          console.error("Failed to mark notifications as read:", error);
        }
      }

      const legacyReadMap = notifications
        .filter((item) => String(item.id).startsWith("legacy-"))
        .reduce((acc, item) => {
          acc[item.id] = true;
          return acc;
        }, {});

      if (Object.keys(legacyReadMap).length) {
        setReadState({
          ...getReadState(),
          ...legacyReadMap,
        });
      }

      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
      return;
    }

    const nextRead = notifications.reduce((acc, item) => {
      acc[item.id] = true;
      return acc;
    }, {});

    setReadState({
      ...getReadState(),
      ...nextRead,
    });

    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
  };

  return {
    notifications,
    unreadCount,
    markAllAsRead,
    usingTableSource,
  };
};
